import { Fragment, h } from 'preact'
import { useEffect, useMemo } from 'preact/hooks'

import { useOrganizationSupporters } from 'lib/organizationSupportHooks'

import Header from 'components/Header'
import Loading from 'components/Loading'
import ErrorMessage from 'components/ErrorMessage'
import Button from 'components/Button'
import Icon from 'components/Icon'
import HeaderedContentBox from 'components/HeaderedContentBox'
import LinkToPublicProfile from 'components/LinkToPublicProfile'
import EndUserAvatar from 'components/EndUserAvatar'

import './index.sass'

export default function SupportersPage({ organization }) {
  const {
    supporters,
    loadingSupporters,
    supportersError,
    pagination,
    loadSupporters,
    clearError,
  } = useOrganizationSupporters(organization.apikey, 'SupportersPage')

  useEffect(() => {
    loadSupporters(1)
  }, [organization.apikey])

  const supporterStats = useMemo(() => {
    const supporterMap = new Map()
    let totalAmount = 0

    supporters.forEach(s => {
      totalAmount += s.amount_cents
      const existing = supporterMap.get(s.supporter_user_did) || { count: 0, total: 0 }
      supporterMap.set(s.supporter_user_did, {
        count: existing.count + 1,
        total: existing.total + s.amount_cents
      })
    })

    return {
      uniqueSupporters: supporterMap.size,
      totalAmount,
      supporterDetails: supporterMap
    }
  }, [supporters])

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const exportToCsv = () => {
    const csvContent = [
      ['Supporter Name', 'Support Date', 'Amount', 'Currency', 'Type', 'Status', 'Next Billing Date'].join(','),
      ...supporters.map(s => [
        `"${s.supporter_name || 'Unknown'}"`,
        new Date(s.support_date).toISOString(),
        (s.amount_cents / 100).toFixed(2),
        s.currency.toUpperCase(),
        s.is_recurring ? `Monthly (${s.recurring_interval})` : 'One-time',
        s.status,
        s.next_billing_date ? new Date(s.next_billing_date).toISOString() : 'N/A'
      ].join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute('download', `${organization.apikey}-supporters-${Date.now()}.csv`)
    link.style.visibility = 'hidden'

    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  if (!organization.support_enabled) {
    return <div className="SupportersPage">
      <HeaderedContentBox>
        <div className="SupportersPage-empty">
          <Icon type="heart" size="lg" style={{ color: '#ccc', marginBottom: '1rem' }} />
          <Header size="md">Support Feature Not Enabled</Header>
          <p>
            Enable the support feature in the Settings tab to allow members to support your hub.
          </p>
        </div>
      </HeaderedContentBox>
    </div>
  }

  return <div className="SupportersPage">
    {supportersError && <ErrorMessage error={supportersError} onDismiss={clearError} />}

    {loadingSupporters && <Loading />}

    {!loadingSupporters && supporters.length === 0 && (
      <HeaderedContentBox>
        <div className="SupportersPage-empty">
          <Icon type="heart" size="lg" style={{ color: '#ccc', marginBottom: '1rem' }} />
          <Header size="md">No Supporters Yet</Header>
          <p>
            When members support your hub, they'll appear here with their support details.
          </p>
        </div>
      </HeaderedContentBox>
    )}

    {!loadingSupporters && supporters.length > 0 && (
      <div className="SupportersPage-content">
        <div className="SupportersPage-header">
          <Button
            type="normal"
            onClick={exportToCsv}
          >
            <Icon type="downloadIcon" /> Export CSV
          </Button>
        </div>

        <div className="SupportersPage-stats">
          <div className="stat">
            <div className="stat-value">{supporterStats.uniqueSupporters}</div>
            <div className="stat-label">Unique Supporters</div>
          </div>
          <div className="stat">
            <div className="stat-value">
              ${(supporterStats.totalAmount / 100).toFixed(2)}
            </div>
            <div className="stat-label">Total Support</div>
          </div>
          <div className="stat">
            <div className="stat-value">{supporters.length}</div>
            <div className="stat-label">Total Contributions</div>
          </div>
        </div>

        <div className="SupportersPage-table">
          <table>
            <thead>
              <tr>
                <th>Supporter</th>
                <th>Type</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Date</th>
              </tr>
            </thead>
            <tbody>
              {supporters.map(supporter => {
                const supporterDetail = supporterStats.supporterDetails.get(supporter.supporter_user_did)
                const supportType = supporter.is_recurring ? 'recurring' : 'onetime'
                return (
                  <tr key={supporter.id} className={`status-${supporter.status}`}>
                    <td className="supporter-info">
                      <div className="supporter-profile">
                        <EndUserAvatar
                          publicProfile={{
                            avatar: supporter.supporter_avatar,
                            username: supporter.supporter_name
                          }}
                          size="sm"
                        />
                        <div className="supporter-details">
                          <LinkToPublicProfile publicProfile={{
                            username: supporter.supporter_username,
                            displayName: supporter.supporter_display_name
                          }}>
                            {supporter.supporter_name || 'Anonymous'}
                          </LinkToPublicProfile>
                          {supporterDetail && supporterDetail.count > 1 && (
                            <span className="supporter-count">
                              {supporterDetail.count} contributions • ${(supporterDetail.total / 100).toFixed(2)} total
                            </span>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="support-type">
                      <span className={`type-badge type-${supportType}`}>
                        {supporter.is_recurring ? (
                          <Fragment>
                            <Icon type="refresh" size="sm" />
                            Monthly
                          </Fragment>
                        ) : (
                          <Fragment>
                            <Icon type="dollar" size="sm" />
                            One-time
                          </Fragment>
                        )}
                      </span>
                      {supporter.is_recurring && supporter.next_billing_date && (
                        <div className="next-billing">
                          Next: {
                            new Date(
                              supporter.next_billing_date
                            ).toLocaleDateString('en-US',
                              { month: 'short', day: 'numeric', year: 'numeric' })
                          }
                        </div>
                      )}
                    </td>
                    <td className="amount">
                      ${(supporter.amount_cents / 100).toFixed(2)}
                    </td>
                    <td className="support-status">
                      <span className={`status-badge status-${supporter.status}`}>
                        {supporter.status}
                      </span>
                    </td>
                    <td className="support-date">
                      {formatDate(supporter.support_date)}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {pagination.totalPages > 1 && (
          <div className="SupportersPage-pagination">
            <Button
              type="normal"
              disabled={pagination.page <= 1}
              onClick={() => loadSupporters(pagination.page - 1)}
            >
              Previous
            </Button>
            <span className="page-info">
              Page {pagination.page} of {pagination.totalPages}
            </span>
            <Button
              type="normal"
              disabled={pagination.page >= pagination.totalPages}
              onClick={() => loadSupporters(pagination.page + 1)}
            >
              Next
            </Button>
          </div>
        )}
      </div>
    )}
  </div>
}
